<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('edit-medication') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 221px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-text-field outlined v-model="dosage" :label="$t('dosage')" hide-details />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						outlined
						v-model="frequency"
						:label="$t('frequency')"
						:items="frequencies"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-text-field
						outlined
						v-model="durationDays"
						:label="$t('duration-days')"
						hide-details
						:suffix="$t('days')"
						@keypress="filterNumbersOnly" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						outlined
						v-model="prescribingDoctor"
						:label="$t('prescribing-doctor')"
						:items="doctors"
						return-object
						item-text="fullName"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-textarea
						v-model="additionalInfo"
						:label="$t('additional-info')"
						rows="10"
						outlined
						dense
						no-resize
						hide-details />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')"
						:dense="false" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip v-if="!editButtonEnabled" left>
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-medication-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="editPrescription()" color="dark_purple" style="color: white" block>{{ $t('update') }} </v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { getRowsForDataTable } from '@/utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMMedicationEventEdit',
	data () {
		return {
			loading: false,
			dosage: '',
			frequency: '',
			frequencies: [],
			period: '',
			additionalInfo: '',
			doctors: null,
			prescribingDoctor: null,
			durationDays: 0,
			route: '',
			isMedicationHasEnded: false,
			additionalData: '',
			timeSpentSeconds: 0,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			detailedUser: (state) => state.users.detailedUser,
			prescriptions: (state) => state.prescriptions.prescriptions,
			searchedPrescriptions: (state) => state.prescriptions.searchedPrescriptions,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			ccmMedicationEvent: (state) => state.ccmMedicationEvents.ccmMedicationEvent,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		...mapActions({
			updateCCMMedicationEvent: 'ccmMedicationEvents/updateCCMMedicationEvent',
		}),
		textareaRows () {
			return this.$vuetify.breakpoint.lgAndUp ? 30 : 19;
		},
		headers () {
			return [
				{
					text: this.$t('prescription-brand-name'),
					value: 'brandName',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('dosage'),
					value: 'dosageInstructions',
					class: 'table-header',
					align: 'start pl-2 pr-0',
				},
				{
					text: this.$t('route'),
					value: 'route',
					class: 'table-header pl-2 pr-0',
					sortable: false,
				},
				{
					text: '',
					align: 'start pl-2 pr-0',
					sortable: false,
				},
			];
		},
		editButtonEnabled () {
			return this.durationDays && this.timeSpentSeconds && this.dosage.trim() && this.timeSpentSeconds <= 7200;
		},
	},
	watch: {
		ccmMedicationEvent () {
			this.route = this.ccmMedicationEvent?.route;
			this.dosage = this.ccmMedicationEvent?.dosage;
			this.frequency = this.ccmMedicationEvent?.frequency;
			this.durationDays = this.ccmMedicationEvent?.durationDays;
			this.prescribingDoctor = this.ccmMedicationEvent?.performedBy;
			this.additionalData = this.ccmMedicationEvent?.additionalData;
			this.medicationName = this.ccmMedicationEvent?.medicationName;
			this.additionalInfo = this.ccmMedicationEvent?.additionalinfo;
		},
		deep: true,
	},

	async mounted () {
		if (!this.prescriptions) await this.getPrescriptionsForPatient();

		this.doctors = this.filterDoctors();
		const response = await this.getMedicationFrequencies();
		this.frequencies = response.data.map((f) => f.description);

		this.route = this.ccmMedicationEvent?.route;
		this.dosage = this.ccmMedicationEvent?.dosage;
		this.frequency = this.ccmMedicationEvent?.frequency;
		this.durationDays = this.ccmMedicationEvent?.durationDays;
		this.prescribingDoctor = this.ccmMedicationEvent?.performedBy;
		this.additionalData = this.ccmMedicationEvent?.additionalData;
		this.medicationName = this.ccmMedicationEvent?.medicationName;
		this.additionalInfo = this.ccmMedicationEvent?.additionalinfo;
	},

	methods: {
		...mapActions({
			getMedicationFrequencies: 'ccmRecords/getMedicationFrequencies',
			updateMedication: 'ccmMedicationEvents/updateCCMMedicationEvent',
		}),
		getRowsForDataTable,
		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.$store.dispatch('prescriptions/clearSearchedPrescriptions');
			this.$store.commit('SET_DETAILS_DRAWER', false);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		async getPrescriptionsForPatient () {
			try {
				await this.$store.dispatch('prescriptions/getPrescriptionsByPatientId', this.detailedUser.userData.id);
			} catch (error) {
				this.$store.commit('alerts/add', {
					id: 'addedMedicationEvent',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
		},

		async editPrescription () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				hospitalId: this.currentRecord.hospitalId,
				medicationName: this.medicationName,
				dosage: this.dosage.trim(),
				frequency: this.frequency,
				durationDays: this.durationDays,
				route: this.route,
				eventDate: new Date().toISOString(),
				eventType: '',
				staffId: this.userData.id,
				timeSpentSeconds: this.timeSpentSeconds,
				additionalData: this.additionalData,
				additionalInfo: this.additionalInfo,
			};
			try {
				const payloadBody = {
					id: this.ccmMedicationEvent.id,
					body: body,
				};
				await this.updateMedication(payloadBody);
				await this.getPrescriptionsForPatient();
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedMedicationEvent',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.closeDrawer();
			}
		},

		filterDoctors () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => member.roleId === 3 && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},

		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},
	},
	components: { InputTimeFormatter },
};
</script>
